import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { userManagementApis } from "@services/apis/UserManagement";
import { UpdataUserProfilePayload } from "@services/models/UserManagement/update-user-profile";
import { UserProfile } from "@services/models/UserManagement/user";
import React, { useEffect, useState } from "react";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";
import UserManagementClasses from "../../UserComponents";
import { DatePicker } from "./DatePicker";
import UserProfileSkeleton from "./UserProfileSkeleton";

interface UserProfileSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (success: boolean) => void;
  userId: number;
  accountType: string;
}

const UserProfileDetails: React.FC<UserProfileSliderProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userId,
  accountType,
}) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const userManegementClasses = new UserManagementClasses();

  const [dob, setDob] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [telegramId, setTelegramId] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [legalEntityName, setLegalEntityName] = useState("");
  const [dba, setDba] = useState("");
  const [country, setCountry] = useState("");

  const [initialState, setInitialState] = useState({
    dob: "",
    whatsappNumber: "",
    telegramId: "",
    username: "",
    firstName: "",
    lastName: "",
    legalEntityName: "",
    dba: "",
    country: "",
  });

  const [changeProfileImageSlider, setChangeProfileImageSlider] =
    useState(false);

  const { handleError, handleSuccess } = useSnackbarMessage();

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);

      let type =
        accountType === "institutional" || accountType === "small_merchant"
          ? "merchant"
          : "individual";

      const response = await userManagementApis.getUserDetails(userId, type);
      const data = response.data;

      setProfile(data);

      const newState = {
        dob: data?.dob ?? "",
        whatsappNumber: data?.whatsappNumber ?? "",
        telegramId: data?.telegramId ?? "",
        profileImage: data?.profileImage ?? "",
        username: data?.username ?? "",
        firstName: data?.firstName ?? "",
        lastName: data?.lastName ?? "",
        legalEntityName: data?.kybLegalEntityName ?? "",
        dba: data?.dba ?? "",
        country: data?.country ?? "",
      };

      setDob(newState.dob);
      setWhatsappNumber(newState.whatsappNumber);
      setTelegramId(newState.telegramId);
      setUsername(newState.username);
      setFirstName(newState.firstName);
      setLastName(newState.lastName);
      setLegalEntityName(newState.legalEntityName);
      setDba(newState.dba);
      setCountry(newState.country);
      setInitialState(newState);
      setIsLoading(false);
    } catch {
      setError("Failed to fetch user profile");
      setIsLoading(false);
    }
  };

  const isUpdateButtonActive =
    firstName !== initialState.firstName ||
    lastName !== initialState.lastName ||
    username !== initialState.username ||
    dob !== initialState.dob ||
    whatsappNumber !== initialState.whatsappNumber ||
    telegramId !== initialState.telegramId;

  const handleOnCancelClick = () => {
    setFirstName(initialState.firstName);
    setLastName(initialState.lastName);
    setUsername(initialState.username);
    setDob(initialState.dob);
    setWhatsappNumber(initialState.whatsappNumber);
    setTelegramId(initialState.telegramId);
  };

  const handleOnUpdateClick = async () => {
    if (!isUpdateButtonActive) return;

    try {
      setIsLoading(true);

      const payload: UpdataUserProfilePayload = {
        userId: userId,
        firstName: firstName,
        lastName: lastName,
        userName: username,
        dob: dob,
        whatsappNumber: whatsappNumber,
        telegramId: telegramId,
      };

      const data = await userManagementApis.updateUserProfile(payload);

      handleSuccess({ message: data.msg });

      await fetchUserProfile();
      onConfirm(true);

      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);

      if (error.response?.data?.msg) {
        handleError({ message: error.response.data.msg });
      } else {
        handleError({
          message: "Unable to update profile. Please try again.",
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen) fetchUserProfile();
  }, [isOpen, userId]);

  if (error) {
    return (
      <SlidingPanel isOpen={isOpen} onClose={onClose}>
        <div className="p-4 text-red-500">{error}</div>
      </SlidingPanel>
    );
  }

  return isLoading ? (
    <UserProfileSkeleton />
  ) : (
    <div className="flex flex-col h-full bg-bg-base">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-[16px] flex flex-col gap-[8px]">
        <div className="flex flex-row w-full gap-[16px]">
          <GlobalTextInputField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-[50%]"
            suffixIcon={
              <span className="material-symbols-outlined text-[14px]">
                edit
              </span>
            }
          />

          <GlobalTextInputField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-[50%]"
            suffixIcon={
              <span className="material-symbols-outlined text-[14px]">
                edit
              </span>
            }
          />
        </div>
        {legalEntityName && dba && (
          <div className="flex flex-row w-full gap-[16px]">
            {legalEntityName && (
              <GlobalTextInputField
                label="Legal Entity Name"
                value={legalEntityName}
                readonly
                onChange={(e) => setLegalEntityName(e.target.value)}
                className="w-[50%]"
              />
            )}

            {dba && (
              <GlobalTextInputField
                label="DBA"
                value={dba}
                readonly
                onChange={(e) => setDba(e.target.value)}
                className="w-[50%]"
              />
            )}
          </div>
        )}
        {!username && !country && (
          <div className="flex flex-row w-full gap-[16px]">
            <GlobalTextInputField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-[50%]"
              suffixIcon={
                <span className="material-symbols-outlined text-[14px]">
                  edit
                </span>
              }
            />
            {country && (
              <GlobalTextInputField
                label="Country"
                value={country}
                readonly
                onChange={(e) => setCountry(e.target.value)}
                className="w-[50%]"
              />
            )}
          </div>
        )}
        <div className="flex flex-row w-full gap-[16px]">
          <GlobalTextInputField
            label="WhatsApp Number"
            type="number"
            value={whatsappNumber}
            onChange={(e) => setWhatsappNumber(e.target.value)}
            className="w-[50%]"
            suffixIcon={
              <span className="material-symbols-outlined text-[14px]">
                edit
              </span>
            }
          />

          <GlobalTextInputField
            label="Telegram ID"
            value={telegramId}
            className="w-[50%]"
            suffixIcon={
              <span className="material-symbols-outlined text-[14px]">
                edit
              </span>
            }
            onChange={(e) => setTelegramId(e.target.value)}
            telegram
            leadingElement={
              <div className="p-3 text-[12px] text-text-base">t.me/</div>
            }
          />
        </div>
        <DatePicker value={dob} onChange={setDob} placeholder="YYYY-MM-DD" />
      </div>

      {/* Sticky Bottom Button */}
      <div className="sticky bottom-0 bg-bg-base border-t border-border-subtle z-10">
        {userManegementClasses.updateDeleteButton({
          onUpdate: handleOnUpdateClick,
          onCancel: handleOnCancelClick,
          isDirty: isUpdateButtonActive,
        })}
      </div>
    </div>
  );
};

export default UserProfileDetails;
